import { logger } from '@shared/logging/logger';
import { FEATURES } from './features';

export interface ValidatedEnv {
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
  WS_URL: string;
  ENABLE_WEBSOCKET: boolean;
  SMS_URL: string;
  IS_PRODUCTION: boolean;
}

/**
 * Enterprise Environment Validation system.
 * Evaluates variables gracefully, provides secure defaults, and alerts configurations via logger.
 */
class EnvironmentValidator {
  private validated: ValidatedEnv | null = null;

  public getEnv(): ValidatedEnv {
    if (this.validated) {
      return this.validated;
    }

    const metaEnv = typeof import.meta !== 'undefined' && (import.meta as any).env ? (import.meta as any).env : {};
    const isProd = metaEnv.PROD || false;
    
    // Resolve Supabase vars safely checking both Vite imports and standard variables
    const supabaseUrl = 
      metaEnv.VITE_SUPABASE_URL || 
      (globalThis as any).process?.env?.SUPABASE_URL || 
      '';
      
    const supabaseAnonKey = 
      metaEnv.VITE_SUPABASE_ANON_KEY || 
      (globalThis as any).process?.env?.SUPABASE_ANON_KEY || 
      '';

    // Resolve WS defaults
    const enableWebsocket = FEATURES.websocket;
    
    const defaultWsUrl = typeof window !== 'undefined'
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
      : 'ws://localhost:3000/ws';

    const wsUrl = metaEnv.VITE_WS_URL || defaultWsUrl;
    const smsUrl = metaEnv.VITE_SMS_URL || '';

    // Warn about missing parameters silently to prevent crash loops
    if (!supabaseUrl) {
      logger.warn('env_validation', 'VITE_SUPABASE_URL is not defined! Standard features of Supabase will fail.');
    }
    if (!supabaseAnonKey) {
      logger.warn('env_validation', 'VITE_SUPABASE_ANON_KEY is not defined! Auth and API requests will fail.');
    }

    this.validated = {
      SUPABASE_URL: supabaseUrl,
      SUPABASE_ANON_KEY: supabaseAnonKey,
      WS_URL: wsUrl,
      ENABLE_WEBSOCKET: enableWebsocket,
      SMS_URL: smsUrl,
      IS_PRODUCTION: isProd,
    };

    logger.debug('env_validation', 'Environment parameters loaded and validated standard configuration', {
      hasSupabaseUrl: !!supabaseUrl,
      hasSupabaseKey: !!supabaseAnonKey,
      wsUrl,
      enableWebsocket,
    });

    return this.validated;
  }
}

export const envValidator = new EnvironmentValidator();
export default envValidator;
